import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReconciledSaleItem } from '../../pages/booking/BookingProducts/components/paymentDialog/PartialPayment'
import { IPaymentReconciliationCreate, PaymentReconciliation, PaymentTypes, ProductSaleItem } from '../../logic/api';
import { round2 } from '@/utility/utils';

export interface INewPayment {
  amount: number | null,
  paymentType: PaymentTypes | null,
  description: string,
  bookingId: number | null,
  paymentDate: string | null,
  authCode?: string | null,
  accountNumber?: string | null,
  reconciliations?: IPaymentReconciliationCreate[]
}

interface IPaymentState {
  error: string,
  newPayment: INewPayment,
  accountNumber: string,
  amount: number | null,
  authCode: string,
  bookingId: number | null,
  description: string,
  paymentType?: PaymentTypes,
  reconciledSaleItems: ReconciledSaleItem[],
  reconciliations: PaymentReconciliation[],
  saleItems: ProductSaleItem[],
  status: PaymentStatus,
}

export enum PaymentStatus {
  "Idle" = 0,
  "Ready" = 1
}

const initialState: IPaymentState = {
  error: "",
  newPayment: {
    amount: null,
    paymentType: null,
    description: "",
    bookingId: null,
    paymentDate: null,
    authCode: undefined,
    accountNumber: "",
    reconciliations: [],
  } as INewPayment,
  accountNumber: "",
  amount: 0,
  authCode: "",
  bookingId: null,
  description: "",
  paymentType: undefined,
  reconciledSaleItems: [],
  reconciliations: [],
  saleItems: [],
  status: PaymentStatus.Idle,
};

type InitializeReconciledSaleItemsPayload = {
  saleItems: ProductSaleItem[];
  reconciliations: PaymentReconciliation[];
  floatingPointFixEnabled: boolean;
};

type UpdatePaymentTypePayload = {
  paymentType: PaymentTypes;
  floatingPointFixEnabled: boolean;
};

type UpdateReconciledSaleItemsPayload = {
  saleItems: ReconciledSaleItem[];
  floatingPointFixEnabled: boolean;
};

const usePaymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    resetLocalPaymentData: () => initialState,

    resetReconciledSaleItems: (state) => {
      state.reconciledSaleItems = [];
    },

    updatePayment: (state, action) => {
      state.newPayment = action.payload;
    },

    updatePaymentAccountNumber: (state, action) => {
      state.accountNumber = action.payload;
    },
    updatePaymentAuthCode: (state, action) => {
      state.authCode = action.payload;
    },
    updatePaymentDescription: (state, action) => {
      state.description = action.payload;
    },
    updatePaymentType: (state, action: PayloadAction<UpdatePaymentTypePayload>) => {
      const currentPaymentType = state.paymentType;
      const { paymentType, floatingPointFixEnabled } = action.payload;
      const { saleItems, reconciliations } = state;

      const round = getRoundingLogic(floatingPointFixEnabled);

      if (currentPaymentType === PaymentTypes.refund && paymentType !== PaymentTypes.refund || currentPaymentType !== PaymentTypes.refund && paymentType === PaymentTypes.refund) {
        const reconciledSaleItems = buildReconciledSaleItems(saleItems, reconciliations, paymentType === PaymentTypes.refund, floatingPointFixEnabled);
        state.amount = round(reconciledSaleItems.reduce((total, rsi) => total += rsi.payment, 0));
      }

      state.paymentType = paymentType;
    },

    initializeReconciledSaleItems: (state, action: PayloadAction<InitializeReconciledSaleItemsPayload>) => {
      const { saleItems, reconciliations, floatingPointFixEnabled } = action.payload;
      const isRefund = state.paymentType === PaymentTypes.refund;
      const reconciledSaleItems = buildReconciledSaleItems(saleItems, reconciliations, isRefund, floatingPointFixEnabled);

      const round = getRoundingLogic(floatingPointFixEnabled);

      state.amount = isRefund ?
        round(reconciledSaleItems.reduce((total, rsi) => total += (rsi.total - rsi.outstandingAmount), 0)) :
        round(reconciledSaleItems.reduce((total, rsi) => total += rsi.outstandingAmount, 0));

      state.reconciliations = reconciliations;
      state.saleItems = saleItems;
      state.reconciledSaleItems = reconciledSaleItems;
    },

    prepareNewPayment: (state, action) => {
      const { booking, localizedDate, partialPayment } = action.payload;
      const { newPayment } = state;

      const isRefund = state.paymentType === PaymentTypes.refund;

      if (typeof state.paymentType === 'undefined') {
        return;
      }

      newPayment.accountNumber = state.accountNumber;
      newPayment.amount = state.amount;
      newPayment.authCode = state.authCode;
      newPayment.bookingId = booking.id;
      newPayment.description = state.description;
      newPayment.paymentDate = localizedDate;
      newPayment.paymentType = state.paymentType;
      newPayment.reconciliations = state.reconciledSaleItems.map((rsi: ReconciledSaleItem) => {

        const getAmount = () => {
          if (partialPayment) {
            return rsi.payment;
          }

          if (isRefund) {
            return rsi.total - rsi.outstandingAmount;
          }

          return rsi.outstandingAmount;
        }

        return {
          bookingId: booking.id,
          productSaleItemId: rsi.productSaleItemId,
          amount: getAmount(),
        }
      }).filter(r => {
        return r.amount;
      });

      //slightly hacky, but we need a way to distinguish refund reconciliations without changing the amount
      if (isRefund) {
        newPayment.reconciliations.forEach(item => {
          item.amount *= -1;
          return item;
        });
      }

      // currently commented out due to isNot variables using old state values. Will allow these values to be set all the time and will filter out on API level when returning
      // if (isNotCardPayment) { newPayment.authCode = ""; }
      // if (isNotAccountPayment) { newPayment.accountNumber = ""; }

      state.status = PaymentStatus.Ready;
    },

    updatePaymentStatus: (state, action) => {
      state.status = action.payload as PaymentStatus;
    },

    updateReconciledSaleItems: (state, action: PayloadAction<UpdateReconciledSaleItemsPayload>) => {
      const { saleItems, floatingPointFixEnabled } = action.payload;

      const round = getRoundingLogic(floatingPointFixEnabled);
      state.amount = round(saleItems.reduce((total: number, rsi: ReconciledSaleItem) => total += rsi.payment, 0));

      state.reconciledSaleItems = saleItems;
    }
  },
});

const buildReconciledSaleItems = (saleItems: ProductSaleItem[], reconciliations: PaymentReconciliation[], isRefund?: boolean, floatingPointFixEnabled?: boolean) => {
  const round = getRoundingLogic(floatingPointFixEnabled);
  const initialReconciledSaleItems: ReconciledSaleItem[] = saleItems.map(saleItem => {

    if (!saleItem.voided && !saleItem.prePaid) {
      return JSON.parse(JSON.stringify({
        maxRefund: 0,
        payment: round(saleItem.qty * saleItem.rate),
        productCode: saleItem.productCode,
        productSaleItemId: saleItem.id,
        name: saleItem.name,
        remainingQuantity: saleItem.qty,
        rate: saleItem.rate,
        outstandingAmount: round(saleItem.qty * saleItem.rate),
        total: saleItem.totalCalculated
      }));
    }
  }).filter(rsi => rsi);

  saleItems.forEach(saleItem => {
    const saleItemReconciliations = reconciliations?.length ? reconciliations.filter(r => {
      return (r.productSaleItem.id === saleItem.id)
    }) : [];

    const reconciledSaleItem = initialReconciledSaleItems.find(rsi => rsi.productSaleItemId === saleItem.id);

    if (reconciledSaleItem) {
      saleItemReconciliations.forEach(sir => {
        reconciledSaleItem.maxRefund = round(reconciledSaleItem.maxRefund + Number(sir.amount));
        reconciledSaleItem.outstandingAmount = round(reconciledSaleItem.outstandingAmount - Number(sir.amount));
        if (isRefund) {
          reconciledSaleItem.payment = round(Number(sir.amount));
        } else {
          reconciledSaleItem.payment = round(reconciledSaleItem.payment - Number(sir.amount));
        }
      });

      if (reconciledSaleItem.outstandingAmount && reconciledSaleItem.rate) {
        reconciledSaleItem.remainingQuantity = Math.ceil(reconciledSaleItem.outstandingAmount / reconciledSaleItem.rate);
      }
    }
  });

  return initialReconciledSaleItems;
}

const getRoundingLogic = (floatingPointFixEnabled?: boolean) => {
  if (floatingPointFixEnabled) {
    return round2;
  }

  return (amount: number) => amount;
}

export const {
  resetLocalPaymentData,
  updatePayment,
  updatePaymentAccountNumber,
  updatePaymentAuthCode,
  updatePaymentDescription,
  updatePaymentStatus,
  updatePaymentType,
  prepareNewPayment,
  initializeReconciledSaleItems,
  updateReconciledSaleItems,
} = usePaymentSlice.actions;

export default usePaymentSlice.reducer;
